/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { SHARED_STYLES } from '../css/shared-css.js';
import { ChromeStatusHttpError } from '../js-src/cs-client.js';
import { SummarySuggestionStatusEnum, } from 'chromestatus-openapi';
import './chromedash-summary-diff-view.js';
import { showToastMessage } from './utils.js';
let ChromedashSummaryReviewDialog = class ChromedashSummaryReviewDialog extends LitElement {
    constructor() {
        super(...arguments);
        this.featureId = 0;
        this.currentSummary = '';
        this.suggestion = null;
        this.editedSummary = '';
        this.isEditing = false;
        this.loading = false;
        this.occConflict = false;
        this.newerSuggestionAvailable = false;
        this.errorMessage = null;
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        sl-dialog {
          --width: min(900px, 95vw);
        }

        sl-alert {
          margin-bottom: var(--sl-spacing-medium);
        }

        .dialog-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--sl-spacing-small);
          width: 100%;
        }

        .footer-left {
          display: flex;
          align-items: center;
          gap: var(--sl-spacing-x-small);
        }

        .footer-right {
          display: flex;
          align-items: center;
          gap: var(--sl-spacing-small);
        }

        @media (max-width: 768px) {
          .dialog-footer {
            flex-direction: column-reverse;
            align-items: stretch;
            gap: var(--sl-spacing-medium);
          }

          .footer-left,
          .footer-right {
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            gap: var(--sl-spacing-small);
          }

          .footer-left sl-button {
            width: 100%;
          }

          .footer-right sl-button {
            flex: 1 1 110px;
            min-width: 0;
          }
        }
      `,
        ];
    }
    show() {
        this.dialogEl?.show();
    }
    hide() {
        this.dialogEl?.hide();
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('suggestion')) {
            const newSuggested = this.suggestion?.suggested_summary || '';
            if (this.isEditing && this.editedSummary !== newSuggested) {
                // User has uncommitted edits in flight. Preserve them to avoid silent data loss,
                // and notify the user that a newer server suggestion was received.
                this.newerSuggestionAvailable = true;
            }
            else {
                this.editedSummary = newSuggested;
                this.isEditing = false;
                this.newerSuggestionAvailable = false;
                this.occConflict = false;
                this.errorMessage = null;
            }
        }
    }
    handleLoadNewestSuggestion() {
        this.editedSummary = this.suggestion?.suggested_summary || '';
        this.isEditing = false;
        this.newerSuggestionAvailable = false;
    }
    handleDismissNewerSuggestion() {
        this.newerSuggestionAvailable = false;
    }
    // Extracted API client delegates for modular testing and component extension
    async applySuggestion(featureId, patch) {
        return window.csClient.updateSummarySuggestion(featureId, patch);
    }
    async rejectSuggestion(featureId, patch) {
        return window.csClient.updateSummarySuggestion(featureId, patch);
    }
    async fetchSuggestion(featureId) {
        return window.csClient.getSummarySuggestion(featureId);
    }
    async triggerRegeneration(featureId, force) {
        return window.csClient.triggerSummaryGeneration(featureId, force);
    }
    async handleAccept() {
        if (!this.featureId ||
            typeof this.suggestion?.version_token !== 'number' ||
            this.loading) {
            return;
        }
        try {
            this.loading = true;
            this.errorMessage = null;
            this.occConflict = false;
            // OCC version_token guards against mid-air collision overwrites if another
            // editor or background task modified the suggestion during review.
            const patch = {
                status: SummarySuggestionStatusEnum.APPLIED,
                suggested_summary: this.editedSummary,
                version_token: this.suggestion.version_token,
            };
            const resp = await this.applySuggestion(this.featureId, patch);
            this.dispatchEvent(new CustomEvent('summary-suggestion-applied', {
                bubbles: true,
                composed: true,
                detail: {
                    featureId: this.featureId,
                    summary: this.editedSummary,
                    response: resp,
                },
            }));
            showToastMessage('AI summary applied successfully.');
            this.hide();
        }
        catch (err) {
            this._handleApiError(err);
        }
        finally {
            this.loading = false;
        }
    }
    async handleReject() {
        if (!this.featureId ||
            typeof this.suggestion?.version_token !== 'number' ||
            this.loading) {
            return;
        }
        try {
            this.loading = true;
            this.errorMessage = null;
            this.occConflict = false;
            const patch = {
                status: SummarySuggestionStatusEnum.REJECTED,
                version_token: this.suggestion.version_token,
            };
            const resp = await this.rejectSuggestion(this.featureId, patch);
            this.dispatchEvent(new CustomEvent('summary-suggestion-rejected', {
                bubbles: true,
                composed: true,
                detail: {
                    featureId: this.featureId,
                    response: resp,
                },
            }));
            showToastMessage('AI summary discarded.');
            this.hide();
        }
        catch (err) {
            this._handleApiError(err);
        }
        finally {
            this.loading = false;
        }
    }
    async handleRegenerate() {
        if (!this.featureId || this.loading)
            return;
        try {
            this.loading = true;
            this.errorMessage = null;
            this.occConflict = false;
            await this.triggerRegeneration(this.featureId, true);
            this.dispatchEvent(new CustomEvent('summary-generation-requested', {
                bubbles: true,
                composed: true,
                detail: { featureId: this.featureId, force: true },
            }));
            showToastMessage('Summary regeneration enqueued.');
            this.hide();
        }
        catch (err) {
            this._handleApiError(err);
        }
        finally {
            this.loading = false;
        }
    }
    async handleRefresh() {
        if (!this.featureId || this.loading)
            return;
        try {
            this.loading = true;
            this.errorMessage = null;
            this.occConflict = false;
            this.newerSuggestionAvailable = false;
            const resp = await this.fetchSuggestion(this.featureId);
            this.suggestion = resp.suggestion ?? null;
            this.editedSummary = this.suggestion?.suggested_summary || '';
            this.isEditing = false;
        }
        catch (err) {
            this._handleApiError(err);
        }
        finally {
            this.loading = false;
        }
    }
    _handleApiError(err) {
        if (err instanceof ChromeStatusHttpError && err.status === 409) {
            this.occConflict = true;
            this.errorMessage =
                'This suggestion was modified in another session. Please refresh to view the latest version.';
        }
        else {
            const error = err instanceof Error ? err : new Error(String(err));
            this.errorMessage = error.message || 'An unexpected error occurred.';
        }
    }
    _renderAlertBanner() {
        if (this.occConflict) {
            return html `
        <sl-alert variant="warning" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          <span>${this.errorMessage}</span>
          <sl-button
            size="small"
            variant="text"
            @click=${this.handleRefresh}
            ?disabled=${this.loading}
          >
            Refresh
          </sl-button>
        </sl-alert>
      `;
        }
        if (this.newerSuggestionAvailable) {
            return html `
        <sl-alert variant="primary" open>
          <sl-icon slot="icon" name="info-circle"></sl-icon>
          <span>A newer suggestion is available on the server.</span>
          <sl-button
            size="small"
            variant="text"
            @click=${this.handleLoadNewestSuggestion}
          >
            Load Newest
          </sl-button>
          <sl-button
            size="small"
            variant="text"
            @click=${this.handleDismissNewerSuggestion}
          >
            Keep My Edits
          </sl-button>
        </sl-alert>
      `;
        }
        if (this.errorMessage) {
            return html `
        <sl-alert variant="danger" open>
          <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
          <span>${this.errorMessage}</span>
        </sl-alert>
      `;
        }
        return nothing;
    }
    _renderFooter() {
        const isAcceptDisabled = this.loading ||
            !this.suggestion ||
            typeof this.suggestion.version_token !== 'number' ||
            this.occConflict;
        const isDiscardDisabled = this.loading ||
            !this.suggestion ||
            typeof this.suggestion.version_token !== 'number';
        return html `
      <div slot="footer" class="dialog-footer">
        <div class="footer-left">
          <sl-button
            variant="default"
            size="small"
            @click=${this.handleRegenerate}
            ?loading=${this.loading}
            ?disabled=${this.loading}
          >
            <sl-icon slot="prefix" name="arrow-clockwise"></sl-icon>
            Regenerate
          </sl-button>
        </div>
        <div class="footer-right">
          <sl-button
            variant="default"
            size="small"
            @click=${this.hide}
            ?disabled=${this.loading}
          >
            Cancel
          </sl-button>
          <sl-button
            variant="danger"
            size="small"
            @click=${this.handleReject}
            ?loading=${this.loading}
            ?disabled=${isDiscardDisabled}
          >
            Discard
          </sl-button>
          <sl-button
            variant="primary"
            size="small"
            @click=${this.handleAccept}
            ?loading=${this.loading}
            ?disabled=${isAcceptDisabled}
          >
            Accept & Apply
          </sl-button>
        </div>
      </div>
    `;
    }
    render() {
        return html `
      <sl-dialog label="Review AI Summary Suggestion">
        ${this._renderAlertBanner()}

        <chromedash-summary-diff-view
          .currentSummary=${this.currentSummary}
          .suggestedSummary=${this.editedSummary}
          .suggestedDocLinks=${this.suggestion?.suggested_doc_links ?? []}
          .isEditing=${this.isEditing}
          .disabled=${this.loading}
          @summary-edit-toggle=${(e) => {
            this.isEditing = e.detail.isEditing;
            this.editedSummary = e.detail.value;
        }}
          @summary-value-change=${(e) => {
            this.editedSummary = e.detail.value;
        }}
        ></chromedash-summary-diff-view>

        ${this._renderFooter()}
      </sl-dialog>
    `;
    }
};
__decorate([
    property({ type: Number })
], ChromedashSummaryReviewDialog.prototype, "featureId", void 0);
__decorate([
    property({ type: String })
], ChromedashSummaryReviewDialog.prototype, "currentSummary", void 0);
__decorate([
    property({ attribute: false })
], ChromedashSummaryReviewDialog.prototype, "suggestion", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "editedSummary", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "isEditing", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "loading", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "occConflict", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "newerSuggestionAvailable", void 0);
__decorate([
    state()
], ChromedashSummaryReviewDialog.prototype, "errorMessage", void 0);
__decorate([
    query('sl-dialog')
], ChromedashSummaryReviewDialog.prototype, "dialogEl", void 0);
ChromedashSummaryReviewDialog = __decorate([
    customElement('chromedash-summary-review-dialog')
], ChromedashSummaryReviewDialog);
export { ChromedashSummaryReviewDialog };
